import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../config/httpConfig";

const postWithToken = (url, body) => axios.post(url, body);

export const busGetIndBusAtList = createAsyncThunk(
  "enqMngtKyc/busGetIndBusAtList",
  async ({ token }, { rejectWithValue }) => {
    try {
      const response = await postWithToken("/api/admin/busGetIndBusAtList", {
        token,
      });
      return response.response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const busVerifyBusPan = createAsyncThunk(
  "enqMngtKyc/busVerifyBusPan",
  async ({ orgId, pan, busName, token }, { rejectWithValue }) => {
    try {
      return await postWithToken("/api/admin/busVerifyBusPan", {
        orgId,
        busName,
        pan,
        token,
      });
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const busVerifyBusGstin = createAsyncThunk(
  "enqMngtKyc/busVerifyBusGstin",
  async ({ orgId, gstin, token }, { rejectWithValue }) => {
    try {
      return await postWithToken("/api/admin/busVerifyBusGstin", {
        orgId,
        gstin,
        token,
      });
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const busVerifyIfsc = createAsyncThunk(
  "enqMngtKyc/busVerifyIfsc",
  async ({ orgId, ifscCode, token }, { rejectWithValue }) => {
    try {
      return await postWithToken("/api/admin/busVerifyIfsc", {
        ifscCode,
        orgId,
        token,
      });
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const busVerifyBank = createAsyncThunk(
  "enqMngtKyc/busVerifyBank",
  async (
    { orgId, ifsc, accountNumber, accountName, token },
    { rejectWithValue },
  ) => {
    try {
      return await postWithToken("/api/admin/busVerifyBank", {
        ifscId: ifsc,
        accNum: accountNumber,
        orgId,
        accHolderName: accountName,
        token,
      });
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const busGetPendingKyc = createAsyncThunk(
  "enqMngtKyc/busGetPendingKyc",
  async ({ orgId, token }, { rejectWithValue }) => {
    try {
      const response = await postWithToken(
        `/api/admin/busGetPendingKyc?orgId=${orgId}`,
        { token },
      );
      return response.response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const busKycHistory = createAsyncThunk(
  "enqMngtKyc/busKycHistory",
  async ({ orgId, token }, { rejectWithValue }) => {
    try {
      const response = await postWithToken(
        `/api/admin/busKycHistory?orgId=${orgId}`,
        { token },
      );
      return response.response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const busKycSubmit = createAsyncThunk(
  "enqMngtKyc/busKycSubmit",
  async (payload, { rejectWithValue }) => {
    try {
      return await postWithToken("/api/admin/busKycSubmit", payload);
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const busGetConsentDigilocker = createAsyncThunk(
  "enqMngtKyc/busGetConsentDigilocker",
  async ({ orgId, token, dealCategoryId }, { rejectWithValue }) => {
    const dealCategoryQuery = dealCategoryId
      ? `&dealCategoryId=${dealCategoryId}`
      : "";
    try {
      const response = await postWithToken(
        `/api/admin/busGetConsentDigilocker?orgId=${orgId}${dealCategoryQuery}`,
        { token },
      );
      return response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

export const busGetAadhaarAndPan = createAsyncThunk(
  "enqMngtKyc/busGetAadhaarAndPan",
  async ({ orgId, token, dealCategoryId }, { rejectWithValue }) => {
    const dealCategoryQuery = dealCategoryId
      ? `&dealCategoryId=${dealCategoryId}`
      : "";
    try {
      const response = await postWithToken(
        `/api/admin/busGetAadhaarAndPan?orgId=${orgId}${dealCategoryQuery}`,
        { token },
      );
      return response;
    } catch (err) {
      return rejectWithValue(err || "An error occurred");
    }
  },
);

// Aliases for shared KYC components (token-only flow in business portal)
export const verifyPan = busVerifyBusPan;
export const verifyBusGstin = busVerifyBusGstin;
export const verifyIfsc = busVerifyIfsc;
export const verifyBank = busVerifyBank;
export const kycHistory = busKycHistory;
export const getAadhaarAndPan = busGetAadhaarAndPan;
export const getConsentDigilocker = busGetConsentDigilocker;

const initialState = {
  loading: false,
  error: "",
  panError: "",
  gstinError: "",
  panLoading: false,
  gstinLoading: false,
  ifscLoading: false,
  bankLoading: false,
  digilockerLoading: false,
  dropdownData: {},
  pendingKycData: {},
  kycHistoryData: {},
  ifscData: {},
  panData: {},
  pendingKycSubmitLoading: false,
  isLinkExpired: false,
};

const enqMngtKycSlice = createSlice({
  name: "enqMngtKyc",
  initialState,
  reducers: {
    updateErrorStatus(state) {
      state.error = "";
      state.panError = "";
      state.gstinError = "";
      state.isLinkExpired = false;
    },
    setLinkExpired(state, action) {
      state.isLinkExpired = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(busGetIndBusAtList.pending, (state) => {
        state.loading = true;
        state.error = "";
      })
      .addCase(busGetIndBusAtList.fulfilled, (state, action) => {
        state.loading = false;
        state.dropdownData = action.payload;
      })
      .addCase(busGetIndBusAtList.rejected, (state, action) => {
        state.loading = false;
        state.dropdownData = {};
        state.error = action.payload;
      })
      .addCase(busVerifyBusPan.pending, (state) => {
        state.panLoading = true;
        state.panError = "";
      })
      .addCase(busVerifyBusPan.fulfilled, (state, action) => {
        state.panLoading = false;
        state.panData = action.payload;
      })
      .addCase(busVerifyBusPan.rejected, (state, action) => {
        state.panLoading = false;
        state.panData = {};
        state.panError = action.payload;
      })
      .addCase(busVerifyBusGstin.pending, (state) => {
        state.gstinLoading = true;
        state.gstinError = "";
      })
      .addCase(busVerifyBusGstin.fulfilled, (state) => {
        state.gstinLoading = false;
      })
      .addCase(busVerifyBusGstin.rejected, (state, action) => {
        state.gstinLoading = false;
        state.gstinError = action.payload;
      })
      .addCase(busVerifyIfsc.pending, (state) => {
        state.ifscLoading = true;
      })
      .addCase(busVerifyIfsc.fulfilled, (state, action) => {
        state.ifscLoading = false;
        state.ifscData = action.payload.response;
      })
      .addCase(busVerifyIfsc.rejected, (state) => {
        state.ifscLoading = false;
        state.ifscData = {};
      })
      .addCase(busVerifyBank.pending, (state) => {
        state.bankLoading = true;
      })
      .addCase(busVerifyBank.fulfilled, (state) => {
        state.bankLoading = false;
      })
      .addCase(busVerifyBank.rejected, (state, action) => {
        state.bankLoading = false;
        state.error = action.payload;
      })
      .addCase(busGetPendingKyc.pending, (state) => {
        state.loading = true;
        state.error = "";
        state.isLinkExpired = false;
      })
      .addCase(busGetPendingKyc.fulfilled, (state, action) => {
        state.loading = false;
        state.isLinkExpired = false;
        state.pendingKycData = action.payload;
        if (action.payload?.kycDetails) {
          state.kycHistoryData = {
            kycDetails: action.payload.kycDetails,
            directorName: action.payload.directorName,
            orgDets: action.payload,
          };
        }
      })
      .addCase(busGetPendingKyc.rejected, (state, action) => {
        state.loading = false;
        state.pendingKycData = {};
        state.error = action.payload;
        if (
          action.payload?.isLinkExpired ||
          (typeof action.payload === "string" && action.payload.toLowerCase().includes("expired")) ||
          (typeof action.error?.message === "string" && action.error.message.toLowerCase().includes("expired"))
        ) {
          state.isLinkExpired = true;
        }
      })
      .addCase(busKycHistory.pending, (state) => {
        state.loading = true;
      })
      .addCase(busKycHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.kycHistoryData = action.payload;
      })
      .addCase(busKycHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(busKycSubmit.pending, (state) => {
        state.pendingKycSubmitLoading = true;
        state.error = "";
      })
      .addCase(busKycSubmit.fulfilled, (state) => {
        state.pendingKycSubmitLoading = false;
      })
      .addCase(busKycSubmit.rejected, (state, action) => {
        state.pendingKycSubmitLoading = false;
        state.error = action.payload;
      })
      .addCase(busGetConsentDigilocker.pending, (state) => {
        state.digilockerLoading = true;
      })
      .addCase(busGetConsentDigilocker.fulfilled, (state) => {
        state.digilockerLoading = false;
      })
      .addCase(busGetConsentDigilocker.rejected, (state, action) => {
        state.digilockerLoading = false;
        state.error = action.payload;
      });
  },
});

export const { updateErrorStatus, setLinkExpired } = enqMngtKycSlice.actions;

export default enqMngtKycSlice.reducer;
